
<template>
  <div class="font-inter bg-white min-h-screen mt-3 rounded-t-[20px]">
    <HeaderComponent page-title="Talent Intelligence" />

    <!-- Header Section -->
    <div class="px-6 pt-6 pb-4">
      <div class="flex items-center justify-between mb-1">
        <div>
          <h1 class="text-[24px] font-semibold text-primaryDark mb-1">
            Hire Verified Talent
          </h1>
          <p class="text-[13px] text-grayDark">
            Source, evaluate, and engage high-quality talent using real-time
            market intelligence
          </p>
        </div>
        <div class="flex items-center gap-3 flex-shrink-0">
          <button
            @click="showSampleTalents"
            class="px-4 py-2 border border-grayLight rounded-lg text-[13px] font-medium text-primaryDark hover:bg-gray-50 transition-colors"
          >
            {{ talents.length > 0 ? 'Clear Talent' : 'View Sample Talent' }}
          </button>
          <button
            class="px-4 py-2 bg-black text-white rounded-lg text-[13px] font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_21422_38183)">
                <mask
                  id="mask0_21422_38183"
                  style="mask-type: luminance"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="16"
                  height="16"
                >
                  <path d="M16 0H0V16H16V0Z" fill="white" />
                </mask>
                <g mask="url(#mask0_21422_38183)">
                  <path
                    d="M8.00073 11.5659C8.60086 11.5659 9.0874 11.0794 9.0874 10.4792C9.0874 9.87911 8.60086 9.39258 8.00073 9.39258C7.4006 9.39258 6.91406 9.87911 6.91406 10.4792C6.91406 11.0794 7.4006 11.5659 8.00073 11.5659Z"
                    fill="white"
                  />
                  <path
                    d="M12.1873 6.35301V5.51967C12.1873 3.71967 11.754 1.33301 8.00065 1.33301C4.24732 1.33301 3.81398 3.71967 3.81398 5.51967V6.35301C1.94732 6.58634 1.33398 7.53301 1.33398 9.85967V11.0997C1.33398 13.833 2.16732 14.6663 4.90065 14.6663H11.1006C13.834 14.6663 14.6673 13.833 14.6673 11.0997V9.85967C14.6673 7.53301 14.054 6.58634 12.1873 6.35301ZM8.00065 12.493C6.88732 12.493 5.98732 11.5863 5.98732 10.4797C5.98732 9.36634 6.89398 8.46634 8.00065 8.46634C9.10732 8.46634 10.014 9.37301 10.014 10.4797C10.014 11.593 9.11398 12.493 8.00065 12.493ZM4.90065 6.29301C4.84732 6.29301 4.80065 6.29301 4.74732 6.29301V5.51967C4.74732 3.56634 5.30065 2.26634 8.00065 2.26634C10.7007 2.26634 11.254 3.56634 11.254 5.51967V6.29967C11.2007 6.29967 11.154 6.29967 11.1006 6.29967H4.90065V6.29301Z"
                    fill="white"
                  />
                </g>
              </g>
              <defs>
                <clipPath id="clip0_21422_38183">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>

            Unlock Talent Intelligence
          </button>
        </div>
      </div>

      <!-- Search and Filter Section -->
      <div class="flex gap-3 items-center mt-4">
        <button
          class="flex items-center gap-2 px-4 py-2 border border-grayLight rounded-lg text-[13px] font-medium text-primaryDark hover:bg-gray-50 transition-colors flex-shrink-0"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.25 5.25H15.75"
              stroke="#667085"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M4.5 9H13.5"
              stroke="#667085"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M7.5 12.75H10.5"
              stroke="#667085"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          Filters
        </button>
        <div class="relative" style="width: 274px;">
          <svg
            width="16"
            height="16"
            class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.66732 13.9997C11.1651 13.9997 14.0007 11.1641 14.0007 7.66634C14.0007 4.16854 11.1651 1.33301 7.66732 1.33301C4.16951 1.33301 1.33398 4.16854 1.33398 7.66634C1.33398 11.1641 4.16951 13.9997 7.66732 13.9997Z"
              stroke="#ABABB3"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14.6673 14.6663L13.334 13.333"
              stroke="#ABABB3"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by role, skill, industry or location"
            class="w-full px-4 py-2 pl-9 border border-grayLight rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-primaryDarkBg"
            style="background-color: rgba(242, 244, 246, 0.4);"
            @input="handleSearch"
          />
        </div>
      </div>
    </div>

    <!-- Talent Cards Grid -->
    <div class="px-6 py-4">
      <!-- Loading State -->
      <div
        v-if="loading"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        <div
          v-for="n in 6"
          :key="n"
          class="animate-pulse bg-white border border-grayLight rounded-xl overflow-hidden"
        >
          <div class="h-[200px] bg-gray-100"></div>
          <div class="p-4 space-y-3">
            <div class="flex justify-between">
              <div class="space-y-2 w-2/5">
                <div class="h-4 bg-gray-200 rounded w-full"></div>
                <div class="h-3 bg-gray-200 rounded w-3/4"></div>
                <div class="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div class="space-y-2 w-2/5">
                <div class="h-3 bg-gray-200 rounded w-full"></div>
                <div class="h-8 bg-gray-200 rounded w-full"></div>
                <div class="h-3 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
            <div class="h-9 bg-gray-200 rounded-lg mt-2"></div>
            <div class="h-3 bg-gray-200 rounded w-1/3 mx-auto"></div>
          </div>
        </div>
      </div>

      <!-- Talent Cards -->
      <div
        v-else-if="talents.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        <div
          v-for="talent in talents"
          :key="talent.talent_id"
          class="bg-white border border-grayLight rounded-xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col"
        >
          <!-- Profile Image Section -->
          <div class="relative w-full h-[190px] bg-gray-50 overflow-hidden">
            <div
              class="absolute inset-0 opacity-10"
              style="
                background-image: repeating-linear-gradient(
                  45deg,
                  #d1d5db 0,
                  #d1d5db 1px,
                  transparent 0,
                  transparent 50%
                );
                background-size: 12px 12px;
              "
            ></div>
            <img
              v-if="talent.profile_picture"
              :src="talent.profile_picture"
              :alt="getTalentName(talent)"
              class="w-full h-full object-cover"
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center relative z-10"
            >
              <div
                class="w-20 h-20 bg-primaryDark rounded-full flex items-center justify-center text-white text-[28px] font-semibold shadow"
              >
                {{ getInitials(talent) }}
              </div>
            </div>
          </div>

          <!-- Card Info Section -->
          <div class="p-4 flex flex-col flex-1">
            <!-- Name / Role / Location + Metrics in two columns -->
            <div class="flex gap-3">
              <!-- Left: Identity -->
              <div class="flex-1 min-w-0">
                <h3
                  class="text-[14px] font-semibold text-primaryDark flex items-center gap-1 leading-tight"
                >
                  <span :class="['truncate', talent.is_locked ? 'blur-[4px] select-none' : '']">{{ getTalentName(talent) }}</span>
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <mask
                      id="mask0_21422_38267"
                      style="mask-type: luminance"
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="10"
                      height="10"
                    >
                      <path d="M10 0H0V10H10V0Z" fill="white" />
                    </mask>
                    <g mask="url(#mask0_21422_38267)">
                      <path
                        d="M5.00143 7.22845C5.37652 7.22845 5.6806 6.92441 5.6806 6.54928C5.6806 6.1742 5.37652 5.87012 5.00143 5.87012C4.62635 5.87012 4.32227 6.1742 4.32227 6.54928C4.32227 6.92441 4.62635 7.22845 5.00143 7.22845Z"
                        fill="#222222"
                      />
                      <path
                        d="M7.61732 3.97051V3.44967C7.61732 2.32467 7.34648 0.833008 5.00065 0.833008C2.65482 0.833008 2.38398 2.32467 2.38398 3.44967V3.97051C1.21732 4.11634 0.833984 4.70801 0.833984 6.16217V6.93717C0.833984 8.64551 1.35482 9.16634 3.06315 9.16634H6.93815C8.64648 9.16634 9.16732 8.64551 9.16732 6.93717V6.16217C9.16732 4.70801 8.78398 4.11634 7.61732 3.97051ZM5.00065 7.80801C4.30482 7.80801 3.74232 7.24134 3.74232 6.54967C3.74232 5.85384 4.30898 5.29134 5.00065 5.29134C5.69232 5.29134 6.25898 5.85801 6.25898 6.54967C6.25898 7.24551 5.69648 7.80801 5.00065 7.80801ZM3.06315 3.93301C3.02982 3.93301 3.00065 3.93301 2.96732 3.93301V3.44967C2.96732 2.22884 3.31315 1.41634 5.00065 1.41634C6.68815 1.41634 7.03398 2.22884 7.03398 3.44967V3.93717C7.00065 3.93717 6.97148 3.93717 6.93815 3.93717H3.06315V3.93301Z"
                        fill="#222222"
                      />
                    </g>
                  </svg>
                </h3>
                <p class="text-[12px] text-grayDark mt-0.5 truncate">
                  {{ talent.position || "Professional" }}
                </p>
                <p
                  class="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5"
                >
                  {{ talent.location || "Location" }}
                </p>
              </div>

              <!-- Right: Metrics -->
              <div class="flex-shrink-0 w-[140px] space-y-2">
                <!-- Skill Match -->
                <div>
                  <div class="flex items-center mb-0.5">
                    <span
                      class="text-[9px] font-semibold text-gray-400 uppercase tracking-wider"
                      >Skill Match</span
                    >
                  </div>
                  <div class="flex items-center gap-1.5">
                    <div
                      class="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden"
                    >
                      <div
                        class="h-full bg-gray-800 rounded-full"
                        :style="{ width: (talent.skill_match || 90) + '%' }"
                      ></div>
                    </div>
                    <span
                      class="text-[8px] text-primaryDark leading-none flex-shrink-0"
                      style="border: 1px solid #222222; padding: 2px;"
                      >{{ talent.skill_match || 90 }}%</span
                    >
                  </div>
                </div>

                <!-- Chart label -->
                <div>
                  <span
                    class="text-[9px] font-semibold text-gray-400 uppercase tracking-wider"
                  >
                    {{ talent.chart_label || "Experience Depth" }}
                  </span>
                </div>

                <!-- Sparkline Chart -->
                <div class="w-full h-[36px]">
                  <svg
                    viewBox="0 0 140 36"
                    class="w-full h-full"
                    preserveAspectRatio="none"
                  >
                    <polyline
                      points="0,30 20,22 40,26 60,14 80,18 100,10 120,14 140,6"
                      fill="none"
                      stroke="#374151"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>

                <!-- Chart value -->
                <div class="flex items-center gap-1">
                  <span class="text-[11px] font-bold text-primaryDark">{{
                    talent.rating || "4.75"
                  }}</span>
                  <span
                    v-if="talent.years_experience"
                    class="text-[10px] text-gray-400"
                    >/ {{ talent.years_experience }} yrs</span
                  >
                </div>

                <!-- Market Availability -->
                <div>
                  <p
                    class="text-[9px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5"
                  >
                    Market Availability
                  </p>
                  <div class="flex items-center gap-1">
                    <span
                      class="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"
                    ></span>
                    <span class="text-[11px] text-gray-700 font-medium">{{
                      talent.availability || "Available"
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Spacer -->
            <div class="flex-1"></div>

            <!-- Action Buttons -->
            <div class="mt-4 space-y-1.5">
              <button
                @click="$router.push({ name: 'TalentProfile', params: { id: talent.talent_id || talent.id || 1 } })"
                class="w-full px-4 py-2.5 bg-black text-white rounded-lg text-[12px] font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_21422_38234)">
                    <mask
                      id="mask0_21422_38234"
                      style="mask-type: luminance"
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="10"
                      height="10"
                    >
                      <path d="M10 0H0V10H10V0Z" fill="white" />
                    </mask>
                    <g mask="url(#mask0_21422_38234)">
                      <path
                        d="M5.00143 7.22845C5.37652 7.22845 5.6806 6.92441 5.6806 6.54928C5.6806 6.1742 5.37652 5.87012 5.00143 5.87012C4.62635 5.87012 4.32227 6.1742 4.32227 6.54928C4.32227 6.92441 4.62635 7.22845 5.00143 7.22845Z"
                        fill="white"
                      />
                      <path
                        d="M7.61732 3.97051V3.44967C7.61732 2.32467 7.34648 0.833008 5.00065 0.833008C2.65482 0.833008 2.38398 2.32467 2.38398 3.44967V3.97051C1.21732 4.11634 0.833984 4.70801 0.833984 6.16217V6.93717C0.833984 8.64551 1.35482 9.16634 3.06315 9.16634H6.93815C8.64648 9.16634 9.16732 8.64551 9.16732 6.93717V6.16217C9.16732 4.70801 8.78398 4.11634 7.61732 3.97051ZM5.00065 7.80801C4.30482 7.80801 3.74232 7.24134 3.74232 6.54967C3.74232 5.85384 4.30898 5.29134 5.00065 5.29134C5.69232 5.29134 6.25898 5.85801 6.25898 6.54967C6.25898 7.24551 5.69648 7.80801 5.00065 7.80801ZM3.06315 3.93301C3.02982 3.93301 3.00065 3.93301 2.96732 3.93301V3.44967C2.96732 2.22884 3.31315 1.41634 5.00065 1.41634C6.68815 1.41634 7.03398 2.22884 7.03398 3.44967V3.93717C7.00065 3.93717 6.97148 3.93717 6.93815 3.93717H3.06315V3.93301Z"
                        fill="white"
                      />
                    </g>
                  </g>
                  <defs>
                    <clipPath id="clip0_21422_38234">
                      <rect width="10" height="10" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                View Full Profile
              </button>
              <button
                class="w-full py-1.5 text-[12px] text-gray-600 hover:text-primaryDark transition-colors text-center bg-transparent rounded-lg"
                style="border: 1px solid #EEEEEE; box-shadow: 0 1px 4px 0 rgba(0,0,0,0.08);"
              >
                Save To Shortlist
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="flex flex-col items-center justify-center py-20">
        <h3 class="text-[18px] font-semibold text-primaryDark mb-2">
          No talents found
        </h3>
        <p class="text-[14px] text-grayDark">
          Try adjusting your search or filters
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import HeaderComponent from "../HeaderComponent.vue";

export default {
  name: "TalentIntelligenceComponent",
  components: {
    HeaderComponent,
  },
  data() {
    return {
      talents: [],
      loading: true,
      searchQuery: "",
      sampleTalents: [
        {
          talent_id: 1,
          first_name: "Thomas",
          last_name: "Frank",
          position: "FRONTEND ENGINEER",
          location: "LAGOS, NG",
          skill_match: 90,
          chart_label: "CLIENT SATISFACTION/YOE",
          rating: "4.75/5.0",
          years_experience: 5,
          availability: "Available",
          is_locked: true
        },
        {
          talent_id: 2,
          first_name: "Sarah",
          last_name: "Snyder",
          position: "UX DESIGNER",
          location: "ABUJA, NG",
          skill_match: 85,
          chart_label: "DESIGN DEPTH",
          rating: "4.90/5.0",
          years_experience: 4,
          availability: "Available",
          is_locked: true
        },
        {
          talent_id: 3,
          first_name: "Michael",
          last_name: "Scott",
          position: "PRODUCT MANAGER",
          location: "LAGOS, NG",
          skill_match: 95,
          chart_label: "LEADERSHIP SCORE",
          rating: "4.85/5.0",
          years_experience: 10,
          availability: "Available",
          is_locked: true
        },
        {
          talent_id: 4,
          first_name: "Jim",
          last_name: "Halpert",
          position: "SALES LEAD",
          location: "IBADAN, NG",
          skill_match: 88,
          chart_label: "REVENUE GROWTH",
          rating: "4.80/5.0",
          years_experience: 6,
          availability: "Available",
          is_locked: true
        },
        {
          talent_id: 5,
          first_name: "Pam",
          last_name: "Beesly",
          position: "RECEPTIONIST",
          location: "LAGOS, NG",
          skill_match: 92,
          chart_label: "CUSTOMER SATISFACTION",
          rating: "5.00/5.0",
          years_experience: 8,
          availability: "Available",
          is_locked: true
        },
        {
          talent_id: 6,
          first_name: "Dwight",
          last_name: "Schrute",
          position: "ASSISTANT REGIONAL MANAGER",
          location: "LAGOS, NG",
          skill_match: 99,
          chart_label: "BEET SALES",
          rating: "4.95/5.0",
          years_experience: 12,
          availability: "Available",
          is_locked: true
        }
      ]
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
  },
  mounted() {
    this.fetchTalents();
  },
  methods: {
    async fetchTalents() {
      this.loading = true;
      try {
        const response = await apiClient.get("/general/b2b/verified/talents", {
          params: {
            has_expert_achievement: true,
          },
        });
        this.talents = response.data.data || [];
      } catch (error) {
        console.error("Error fetching talents:", error);
      } finally {
        this.loading = false;
      }
    },
    showSampleTalents() {
      if (this.talents.length > 0) {
        this.talents = [];
      } else {
        this.talents = [...this.sampleTalents];
      }
    },
    getTalentName(talent) {
      if (talent.user) {
        return `${talent.user.first_name || ""} ${
          talent.user.last_name || ""
        }`.trim();
      }
      return (
        `${talent.first_name || ""} ${talent.last_name || ""}`.trim() ||
        "Unknown Talent"
      );
    },
    getInitials(talent) {
      const name = this.getTalentName(talent);
      const parts = name.split(" ");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    handleSearch() {
      console.log("Searching for:", this.searchQuery);
    },
  },
};
</script>

<style scoped>
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
